/**
 * DUPR (Dynamic Universal Pickleball Rating) Skill Level Constants
 *
 * Standard scale: 2.00 – 8.00
 *   Beginner:      2.00 – 2.99
 *   Intermediate:  3.00 – 3.99
 *   Advanced:      4.00 – 4.99
 *   Expert/Pro:    5.00 – 8.00
 */

export const DUPR_MIN = 2.0;
export const DUPR_MAX = 8.0;

export interface DuprCategory {
    key: string;
    label: string;
    min: number;
    max: number;
}

export const DUPR_CATEGORIES: DuprCategory[] = [
    { key: 'BEGINNER', label: 'Beginner', min: 2.0, max: 2.99 },
    { key: 'INTERMEDIATE', label: 'Intermediate', min: 3.0, max: 3.99 },
    { key: 'ADVANCED', label: 'Advanced', min: 4.0, max: 4.99 },
    { key: 'EXPERT_PRO', label: 'Expert/Pro', min: 5.0, max: 8.0 },
];

/** @deprecated Use DUPR_CATEGORIES instead */
export const SKILL_LEVELS = DUPR_CATEGORIES.map((c) => ({
    value: c.min,
    label: c.label,
    min: c.min,
    max: c.max,
}));

/**
 * Get the DUPR category label for a given float rating.
 */
export const getDuprLabel = (rating: number | null | undefined): string => {
    if (rating === null || rating === undefined) return 'Not Set';
    const category = DUPR_CATEGORIES.find((c) => rating >= c.min && rating <= c.max);
    return category ? category.label : `Level ${Number(rating).toFixed(2)}`;
};

/**
 * Get a display string like "Intermediate (3.50)" for a rating.
 */
export const getDuprDisplay = (rating: number | null | undefined): string => {
    if (rating === null || rating === undefined) return 'Not Set';
    const label = getDuprLabel(rating);
    return `${label} (${Number(rating).toFixed(2)})`;
};

/**
 * Get a MUI color for a DUPR rating.
 */
export const getDuprColor = (
    rating: number | null | undefined,
): 'default' | 'info' | 'success' | 'warning' | 'error' => {
    if (rating === null || rating === undefined) return 'default';
    if (rating >= 5.0) return 'error';
    if (rating >= 4.0) return 'warning';
    if (rating >= 3.0) return 'info';
    return 'success';
};

/** @deprecated Use getDuprLabel instead */
export const getSkillLevelLabel = (level: number | null): string => getDuprLabel(level);

/**
 * Session level presets for forms.
 */
export const SESSION_LEVEL_PRESETS = [
    { key: 'OPEN', label: 'Open / All Levels', min: null as number | null, max: null as number | null },
    { key: 'BEGINNER', label: 'Beginner (2.00–2.99)', min: 2.0, max: 2.99 },
    { key: 'INTERMEDIATE', label: 'Intermediate (3.00–3.99)', min: 3.0, max: 3.99 },
    { key: 'ADVANCED', label: 'Advanced (4.00–4.99)', min: 4.0, max: 4.99 },
    { key: 'EXPERT_PRO', label: 'Expert/Pro (5.00–8.00)', min: 5.0, max: 8.0 },
];

/**
 * Derive a human-readable label from session min_level / max_level.
 */
export const getSessionLevelLabel = (
    minLevel: number | null | undefined,
    maxLevel: number | null | undefined,
): string => {
    if (minLevel == null && maxLevel == null) return 'Open / All Levels';

    const preset = SESSION_LEVEL_PRESETS.find((p) => p.min === minLevel && p.max === maxLevel);
    if (preset) return preset.label;

    const minStr = minLevel != null ? minLevel.toFixed(2) : '2.00';
    const maxStr = maxLevel != null ? maxLevel.toFixed(2) : '8.00';
    return `${minStr} – ${maxStr}`;
};

/**
 * Convert legacy 1-3 integer skill level to DUPR midpoint for display.
 */
export const legacySkillToDupr = (value: number | null | undefined): number | null => {
    if (value === null || value === undefined) return null;
    if (value >= DUPR_MIN) return value; // Already DUPR
    const map: Record<number, number> = { 1: 2.5, 2: 3.5, 3: 4.5 };
    return map[Math.round(value)] ?? null;
};
